// Table
import { PassThroughSerializationHandler } from "./persistence/PassThroughSerializationHandler";
// Utilities
import isEqual from "lodash/isEqual";
/**
 * BasicTable implementation
 *
 * @deprecated use `table/Table` instead
 */ export class BasicTable {
    get name() {
        return this.tableName;
    }
    // #region CRUD OPERATIONS
    get(id) {
        return this.items[id] ?? null;
    }
    getAll() {
        return this.items;
    }
    set(id, value) {
        return this.setInternal(id, value);
    }
    reKey(id, newId) {
        this.setInternal(newId, this.getNonNull(id));
        this.delete(id);
    }
    delete(id) {
        if (!this.items[id]) {
            return false; // Nothing to delete
        }
        this.applyIndexedUpdate(id, ()=>{
            delete this.items[id];
        });
        this.flag(id);
        return true;
    }
    // #endregion
    // #region INDEXING
    getByIndex(descriptor) {
        const index = this.getIndexes()[descriptor.name];
        if (!index) {
            throw new Error(`IndexNotFoundError [Table=${this.name}][Index=${descriptor.name}][Value=${descriptor.value}]`);
        }
        const indexedItemIds = Array.from(index.data[descriptor.value] ?? []);
        const result = {};
        for (const id of indexedItemIds){
            result[id] = this.getNonNull(id);
        }
        return result;
    }
    getIndexes() {
        return this.indexes;
    }
    // #endregion
    // #region PERSISTENCE
    hydrate(records) {
        // Step 1: Deserialize all records to ensure there are no errors
        /** This is done before populating the data into the table to ensure
         * partial data is not pushed into the table if there are failures in deserialization
         */ const deserializedRecords = {};
        for (const [key, serializableValue] of Object.entries(records)){
            try {
                deserializedRecords[key] = this.serializationHandler.fromSerializable(key, serializableValue);
            } catch (error) {
                throw new Error(`Failed to deserialize record in BasicTable [Table=${this.name}][Key=${key}][Error=${error.message}]`);
            }
        }
        // Step 2: Set deserialized records into the table
        /**
         * Append new items to the existing items, without overwriting. Entries in cache are
         * guaranteed to be older than what is in memory so overwriting is never useful.
         */ let tableModified = false;
        for (const [key, deserializedValue] of Object.entries(deserializedRecords)){
            // Set only if the item is not already in memory
            if (!this.items[key]) {
                this.setInternal(key, deserializedValue, false);
                tableModified = true;
            }
        }
        return tableModified;
    }
    nextModifiedBatch() {
        const batch = {
            upsertedItems: {},
            deletedKeys: new Set()
        };
        // Prepare batch
        for (const key of this.modifiedKeys){
            const item = this.items[key];
            if (item) {
                batch.upsertedItems[key] = this.serializationHandler.toSerializable(key, item);
            } else {
                batch.deletedKeys.add(key);
            }
        }
        // Clear the modified keys after the batch is prepared
        this.modifiedKeys.clear();
        return batch;
    }
    // #endregion
    /**
     * Mark a key as modified
     */ flag(id) {
        this.modifiedKeys.add(id);
    }
    /**
     * Get an item from the table, throwing an error if it does not exist
     * @param id The id of the item
     */ getNonNull(id) {
        const item = this.items[id];
        if (!item) {
            throw new Error(`ItemNotFound [Table=${this.name}][Id=${id}]`);
        }
        return item;
    }
    /**
     * Apply an update and update the indexes
     * @param id The id of the item being updated
     * @param applyUpdate Function that updates the item
     * @param getLatestValue Function that returns the latest value of the item
     */ applyIndexedUpdate(id, applyUpdate, getLatestValue = ()=>this.items[id]) {
        // Step 1 - Calculate index positions before the update
        const currentValue = getLatestValue();
        const indexValues = {};
        const indexKeys = Object.keys(this.indexes);
        for (const key of indexKeys){
            indexValues[key] = currentValue ? this.indexes[key].accessor(currentValue) : [];
        }
        // Step 2 - Apply the update
        applyUpdate();
        // Step 3 - Update the indexes
        const updatedValue = getLatestValue();
        for (const key of indexKeys){
            const index = this.indexes[key];
            // Values that this item is currently indexed under
            const current = indexValues[key];
            // Values that this item should be indexed under
            const target = updatedValue ? index.accessor(updatedValue) : [];
            // Create a map of the target values for faster lookups
            const targetMap = {};
            for (const value of target){
                targetMap[value] = true;
            }
            /**
             * In a single pass-
             * 1. Calculate map of current values for faster lookups
             * 2. Remove id from the index if it is not in the target
             */ const currentMap = {};
            for (const key of current){
                currentMap[key] = true;
                // Remove id from the set if it is not in the target
                if (!targetMap[key]) {
                    if (index.data[key]) {
                        index.data[key].delete(id);
                        // Clean up the set if it is empty
                        if (index.data[key].size === 0) {
                            delete index.data[key];
                        }
                    }
                }
            }
            // Add id to the index if it is in the target but not in the current
            for (const key of target){
                if (!currentMap[key]) {
                    // Create the set if it does not exist
                    if (!index.data[key]) {
                        index.data[key] = new Set();
                    }
                    index.data[key].add(id);
                }
            }
        }
    }
    /**
     * Initialize the indexes from the given index definitions
     * @param indexDefinitions Index definitions
     */ initializeIndexes(indexDefinitions) {
        const indexes = {};
        for (const key of Object.keys(indexDefinitions)){
            indexes[key] = {
                accessor: (item)=>{
                    const value = indexDefinitions[key](item);
                    if (!value) {
                        return [];
                    }
                    if (typeof value === "string") {
                        return [
                            value
                        ];
                    }
                    if (typeof value[Symbol.iterator] === "function") {
                        // Create a copy of the array to prevent mutation
                        return Array.from(value);
                    }
                    return Object.keys(value);
                },
                data: {}
            };
        }
        return indexes;
    }
    /**
     * Helper to set an item in the table
     * @param id The id of the item
     * @param value The value of the item
     * @param shouldFlag Whether to mark the item as modified (default: true)
     */ setInternal(id, value, shouldFlag = true) {
        // If the value is already set, don't do anything
        if (isEqual(this.items[id], value)) {
            return false;
        }
        this.applyIndexedUpdate(id, ()=>{
            this.items[id] = value;
        });
        if (shouldFlag) {
            this.flag(id);
        }
        return true;
    }
    /**
     * Create a new BasicTable
     * @param tableName The name of the table
     * @param indexDefinitions Index definitions for the table
     * @param serializationHandler Serialization handler that defines how entities are are converted to and from a serializable
     */ constructor(tableName, indexDefinitions = {}, serializationHandler = new PassThroughSerializationHandler()){
        this.tableName = tableName;
        this.serializationHandler = serializationHandler;
        this.items = {};
        this.indexes = {};
        this.modifiedKeys = new Set();
        this.indexes = this.initializeIndexes(indexDefinitions);
    }
}
